import { ButtonBase, CircularProgress, Typography } from "@mui/material"
import { DrivingExpirienceDto } from "api"
import { trackEngineModelPageClicks } from "common/analytics"
import dynamic from "next/dynamic"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useCountUp } from "react-countup"
import { useInView } from "react-intersection-observer"
import styled from "styled-components"
import { LongPressDetectEvents, useLongPress } from "use-long-press"
import IgnationIcon from "../../../../../reusable/icons/IgnationIcon"
import InfoIcon from "../../../../../reusable/icons/InfoIcon"
import VolumeIcon from "../../../../../reusable/icons/VolumeIcon"
import VolumeMuteIcon from "../../../../../reusable/icons/VolumeMuteIcon"
import PerformanceNumber from "./PerformanceNumber"

const VideoPlayer = dynamic(() => import("common/VideoPlayer"), {
  loading: () => <p>...</p>,
})

const StyledSectionWrapper = styled.div`
  min-height: 90vh;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  max-width: 450px;
  justify-content: center;
  ${(p) => p.theme.breakpoints.down("sm")} {
    padding-bottom: 80px;
  }
`

const StyledCaption = styled(Typography).attrs({ variant: "h2" })`
  color: ${(p) => p.theme.colors.disable};
  font-size: 18px;
  font-weight: 400;
`

const StyledTitle = styled(Typography).attrs({ variant: "h3" })`
  color: ${(p) => p.theme.colors.black};
  text-align: center;
  font-size: 24px;
  font-weight: 400;
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 90%;
  }
`

const StyledVideoWrapper = styled.div`
  width: 236px;
  height: 253px;
  margin-top: 20px;
  video {
    clip-path: inset(1px 1px);
  }
`

const StyledInfo = styled.div`
  display: flex;
  margin-top: 20px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    overflow: hidden;
    width: 100%;
    justify-content: center;
  }
`
const ActionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 20px;
  justify-content: center;
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 100%;
  }
`

const SvgWrapperButton = styled(ButtonBase)`
  cursor: pointer;
  position: relative;
  width: 140px;
  height: 140px;
  .MuiCircularProgress-root {
    position: absolute;
    width: 100% !important;
    height: 100% !important;
    left: 0px;
    border-radius: 50%;
    background: ${(p) => p.theme.colors.gainsboro};
    box-shadow: 0px 0px 19px rgb(0 0 0 / 22%),
      inset 0px -10px 10px rgb(0 0 0 / 4%);
  }

  .MuiCircularProgress-circleDeterminate {
    color: ${(p) => p.theme.colors.mazdaRed};
  }
`

const StyledButtonDiv = styled.div`
  outline: none;
  border: none;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: none;
  cursor: pointer;
  user-select: none;
  svg {
    position: relative;
    z-index: 1;
  }
`

type PerformanceWrapperType = {
  drivingExpirience: DrivingExpirienceDto
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const PerformanceWrapper = ({
  drivingExpirience,
  setModalOpen,
}: PerformanceWrapperType) => {
  const data = drivingExpirience?.engine[0]

  const [progress, setProgress] = useState(0)
  const [videoPlay, setVideoPlay] = useState(false)
  const [press, setPressed] = useState(false)
  const [longPressFinished, setLongPressFinished] = useState(false)
  const [muted, setMuted] = useState(false)
  const playPromiseRef = useRef<Promise<void> | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  // No placeholder values here. A model with missing engine data renders no figure at all
  // rather than an invented one, which is wrong for electric models in particular.
  const numbers = useMemo(
    () => ({
      first: data?.engineIdName
        ? data.engineIdName.substring(0, data.engineIdName.length - 1)
        : "",
      second: data?.performanceItems?.[1]?.value || "",
      third: data?.performanceItems?.[2]?.value || "",
    }),
    [drivingExpirience]
  )

  const ref1 = useRef(null)
  const ref2 = useRef(null)
  const ref3 = useRef(null)
  const [refInView, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
  })

  const { reset: reset1, update: update1 } = useCountUp({
    ref: ref1,
    start: 0,
    end: 5,
    decimals: 1,
    delay: 1000,
    duration: 5,
    suffix: "L",
  })

  const { reset: reset2, update: update2 } = useCountUp({
    ref: ref2,
    start: 0,
    end: 5,
    decimals: 1,
    delay: 1000,
    duration: 5,
  })

  const { reset: reset3, update: update3 } = useCountUp({
    ref: ref3,
    start: 0,
    end: 5,
    decimals: 1,
    delay: 1000,
    duration: 5,
  })

  const finishedLongPress = useCallback(() => {
    setLongPressFinished(true)
    // trigger after 5 seconds
  }, [])

  const longPressBtnBind = useLongPress(finishedLongPress, {
    //long press events
    onStart: () => onStartPress(),
    onFinish: () => onFinishPress(),
    onCancel: () => onFinishPress(),
    threshold: 5000,
    captureEvent: true,
    cancelOnMovement: false,
    detect: LongPressDetectEvents.BOTH,
  })

  useEffect(() => {
    //fill progress bar on button press
    if (press) {
      const timer = setInterval(() => {
        setProgress((prevProgress) =>
          prevProgress >= 100 ? 100 : prevProgress + 2
        )
      }, 100)
      return () => {
        clearInterval(timer)
      }
    }
  }, [press])

  useEffect(() => {
    if (inView) {
      resetNumbers()
      updateNumbers()
    }
  }, [inView])

  const updateNumbers = () => {
    if (numbers.first) update1(numbers.first.replace(/,/g, "."))
    if (numbers.second) update2(numbers.second.replace(/,/g, "."))
    if (numbers.third) update3(numbers.third.replace(/,/g, "."))
  }

  const resetNumbers = () => {
    reset1()
    reset2()
    reset3()
  }

  const onStartPress = () => {
    toggleAudio()
    setProgress(0)
    setPressed(true)
    setVideoPlay(true)
    setLongPressFinished(false)
    resetNumbers()
    updateNumbers()
    setTimeout(() => {
      if (navigator && navigator.vibrate) {
        navigator.vibrate([
          100, 30, 100, 30, 100, 30, 1000, 500, 1000, 500, 1000,
        ])
      }
    }, 50)
  }

  const onFinishPress = () => {
    setVideoPlay(false)
    toggleAudio()
    if (!longPressFinished) {
      resetNumbers()
    }
    setPressed(false)
    setProgress(0)
  }

  const toggleAudio = async () => {
    if (audioRef && audioRef.current) {
      const audio = audioRef.current

      if (audio.paused) {
        playPromiseRef.current = audio.play()
        try {
          await playPromiseRef.current
        } catch (err) {
          console.error(err)
        }
      } else {
        if (playPromiseRef.current) {
          playPromiseRef.current
            .then(() => {
              audio.pause()
            })
            .catch((err) => {
              console.error(err)
            })
        }
      }
    }
  }

  const toggleAudioMute = () => {
    setMuted(!muted)
  }

  return (
    <StyledSectionWrapper ref={refInView}>
      <audio
        id="myAudio"
        src="/voice/2A-ENGINES.mp3"
        ref={audioRef}
        muted={muted}
      ></audio>
      {!!data?.engineName && <StyledCaption>{data.engineName}</StyledCaption>}
      <StyledTitle>
        {data?.engineDescription
          ? data.engineDescription
          : "חווית נהיגה היא קודם כל מנוע איכותי"}
      </StyledTitle>
      <StyledVideoWrapper>
        <VideoPlayer
          url={data?.engineImage.imageUrl}
          light={false}
          label={"מנוע"}
          playingState={videoPlay}
          loop={true}
          imgAlt={"engine"}
          imgUrlDesktop={"/img/engine.png"}
          imgUrlMobile={"/img/engine.png"}
          onBufferEnd={() => {
            if (videoPlay) {
              setVideoPlay(false)
              setVideoPlay(true)
            }
          }}
        />
      </StyledVideoWrapper>
      <StyledInfo>
        {!!numbers.first && (
          <PerformanceNumber
            reference={ref1}
            text={`נפח (ליטרים)`}
            press={press}
            number={`${parseInt(numbers.first.replace(/,/g, ".")).toFixed(1)}L`}
          />
        )}
        {!!numbers.second && (
          <PerformanceNumber
            reference={ref2}
            text={"הספק (כ״ס)"}
            press={press}
            number={parseInt(numbers.second.replace(/,/g, ".")).toFixed(1)}
          />
        )}
        {!!numbers.third && (
          <PerformanceNumber
            reference={ref3}
            text={"האצה 0-100 (שניות)"}
            press={press}
            number={parseInt(numbers.third.replace(/,/g, ".")).toFixed(1)}
          />
        )}
      </StyledInfo>
      <ActionWrapper>
        <SvgWrapperButton
          onClick={() => setModalOpen(true)}
          disableRipple
          aria-label="more information"
        >
          <InfoIcon />
        </SvgWrapperButton>
        <SvgWrapperButton
          {...longPressBtnBind()}
          disableRipple
          aria-label="start ignation"
        >
          <StyledButtonDiv onClick={() => trackEngineModelPageClicks()}>
            <IgnationIcon />
          </StyledButtonDiv>
          <CircularProgress
            variant="determinate"
            value={press ? progress : 0}
            thickness={6}
          />
        </SvgWrapperButton>

        <SvgWrapperButton
          onClick={() => toggleAudioMute()}
          disableRipple
          aria-label={`${muted ? "unmute" : "mute"}`}
        >
          {muted ? <VolumeMuteIcon /> : <VolumeIcon />}
        </SvgWrapperButton>
      </ActionWrapper>
    </StyledSectionWrapper>
  )
}

export default PerformanceWrapper
